/**
 * Contadores locais de frequência por slot.
 * Persistidos no localStorage para sobreviver a reloads.
 * Resetados diariamente à meia-noite do workspace timezone.
 *
 * Controles suportados:
 *   deviceImpressionCap  — máximo de plays no dia por device
 *   minIntervalSeconds   — intervalo mínimo entre plays consecutivos
 *   pacingDailyTarget    — meta diária de even pacing (para evitar burst de manhã)
 *   sovDailyTarget       — cap diário derivado de SOV × deviceImpressionCap
 */
import type { PlayQueueItem } from '@/types/player.types'

const STORAGE_KEY = 'castiq-player-counters'

interface CounterState {
  date: string // "YYYY-MM-DD" no timezone do workspace
  impressions: Record<string, number> // slotId → count no dia
  lastPlayed: Record<string, number> // slotId → Date.now() da última exibição
}

function getLocalDate(timezone: string): string {
  // 'en-CA' retorna formato YYYY-MM-DD
  return new Intl.DateTimeFormat('en-CA', { timeZone: timezone }).format(new Date())
}

function load(): CounterState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) return JSON.parse(raw) as CounterState
  } catch {
    // corrupted
  }
  return { date: '', impressions: {}, lastPlayed: {} }
}

function save(state: CounterState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch {
    // storage full
  }
}

export type SkipReason = 'cap' | 'interval' | null

export const localCounters = {
  /**
   * Retorna o motivo pelo qual o item deve ser pulado, ou null se pode tocar.
   *
   * 'cap'      — bloqueio permanente no dia (deviceImpressionCap, sovDailyTarget, pacingDailyTarget)
   * 'interval' — cooldown temporário (minIntervalSeconds); expira sozinho
   * null       — pode tocar agora
   */
  skipReason(item: PlayQueueItem, timezone: string): SkipReason {
    const state = load()
    const today = getLocalDate(timezone)

    if (state.date !== today) return null

    const { slotId } = item
    const count = state.impressions[slotId] ?? 0

    if (item.deviceImpressionCap && count >= item.deviceImpressionCap) return 'cap'
    if (item.sovDailyTarget && count >= item.sovDailyTarget) return 'cap'
    if (item.pacingDailyTarget && count >= item.pacingDailyTarget) return 'cap'

    if (item.minIntervalSeconds) {
      const last = state.lastPlayed[slotId]
      if (last && Date.now() - last < item.minIntervalSeconds * 1000) return 'interval'
    }

    return null
  },

  /** Verifica se um item deve ser pulado por cap ou minInterval local. */
  shouldSkip(item: PlayQueueItem, timezone: string): boolean {
    return this.skipReason(item, timezone) !== null
  },

  /** Retorna o timestamp (ms) da última exibição de um slot, ou null se nunca tocou. */
  getLastPlayed(slotId: string): number | null {
    const state = load()
    return state.lastPlayed[slotId] ?? null
  },

  /** Registra uma exibição nos contadores locais. */
  record(slotId: string, timezone: string): void {
    const state = load()
    const today = getLocalDate(timezone)

    if (state.date !== today) {
      state.date = today
      state.impressions = {}
      state.lastPlayed = {}
    }

    state.impressions[slotId] = (state.impressions[slotId] ?? 0) + 1
    state.lastPlayed[slotId] = Date.now()

    save(state)
  },

  /** Remove um slot dos contadores (ao receber capReached da API). */
  markCapReached(slotId: string, timezone: string): void {
    const state = load()
    const today = getLocalDate(timezone)
    if (state.date !== today) return
    // Valor alto garante que shouldSkip retorne true para qualquer cap
    state.impressions[slotId] = 999_999
    save(state)
  }
}
